// Configuración del cobro con De Una (QR Banco Pichincha). POS → Configuración → De Una (QR).
// Las llaves se cifran en reposo (write-only): el cliente solo sabe si están configuradas.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QrCode, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, DeunaConfig, FormaPago, Switch, deunaConfigGet, deunaConfigSet, fieldCls, pos } from './shared';

const vacio = {
  proveedor: 'simulado', ambiente: 'sandbox', merchant_id: '', caja_deuna: '', base_url: '',
  forma_pago_id: '', es_activo: true, api_key: '', api_secret: '',
};

export function ConfigDeuna() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery<DeunaConfig | null>({ queryKey: ['pos-deuna-config'], queryFn: deunaConfigGet, staleTime: 0 });
  const { data: formas = [] } = useQuery<FormaPago[]>({
    queryKey: ['pos-formas'], queryFn: () => pos.list<FormaPago>('formas_pago', '?select=*&deleted_at=is.null&order=orden'),
  });

  const [f, setF] = useState<typeof vacio>(vacio);
  const set = (patch: Partial<typeof vacio>) => setF((s) => ({ ...s, ...patch }));
  useEffect(() => {
    if (cfg) setF({
      proveedor: cfg.proveedor, ambiente: cfg.ambiente, merchant_id: cfg.merchant_id ?? '',
      caja_deuna: cfg.caja_deuna ?? '', base_url: cfg.base_url ?? '', forma_pago_id: cfg.forma_pago_id ?? '',
      es_activo: cfg.es_activo, api_key: '', api_secret: '',
    });
  }, [cfg]);

  const guardar = useMutation({
    mutationFn: () => deunaConfigSet({
      proveedor: f.proveedor, ambiente: f.ambiente, merchant_id: f.merchant_id || null,
      caja_deuna: f.caja_deuna || null, base_url: f.base_url || null,
      forma_pago_id: f.forma_pago_id || null, es_activo: f.es_activo,
      api_key: f.api_key || '', api_secret: f.api_secret || '',   // '' = conservar
    }),
    onSuccess: () => { toast.success('Configuración De Una guardada'); set({ api_key: '', api_secret: '' }); void qc.invalidateQueries({ queryKey: ['pos-deuna-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><QrCode className="w-5 h-5" /> De Una (QR Banco Pichincha)</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Proveedor y credenciales</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Campo label="Proveedor">
              <select className={fieldCls} value={f.proveedor} onChange={(e) => set({ proveedor: e.target.value })}>
                <option value="simulado">Simulado (pruebas)</option>
                <option value="deuna_directo">De Una directo (banco)</option>
                <option value="payvalida">Payvalida (agregador)</option>
              </select>
            </Campo>
            <Campo label="Ambiente">
              <select className={fieldCls} value={f.ambiente} onChange={(e) => set({ ambiente: e.target.value })}>
                <option value="sandbox">Sandbox</option>
                <option value="produccion">Producción</option>
              </select>
            </Campo>
            <Campo label="Merchant ID"><input className={fieldCls} value={f.merchant_id} onChange={(e) => set({ merchant_id: e.target.value })} /></Campo>
            <Campo label="N° de caja De Una"><input className={fieldCls} value={f.caja_deuna} onChange={(e) => set({ caja_deuna: e.target.value })} /></Campo>
          </div>
          <Campo label="URL base (opcional — override del endpoint del proveedor)">
            <input className={fieldCls} value={f.base_url} onChange={(e) => set({ base_url: e.target.value })} placeholder="https://…" />
          </Campo>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Campo label={`API key ${cfg?.api_key_configurada ? '· ✓ configurada (dejar en blanco = conservar)' : ''}`}>
              <input type="password" autoComplete="new-password" className={fieldCls} value={f.api_key} onChange={(e) => set({ api_key: e.target.value })} />
            </Campo>
            <Campo label={`Clave secreta ${cfg?.api_secret_configurada ? '· ✓ configurada (dejar en blanco = conservar)' : ''}`}>
              <input type="password" autoComplete="new-password" className={fieldCls} value={f.api_secret} onChange={(e) => set({ api_secret: e.target.value })} />
            </Campo>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5" /> Las llaves se cifran en la base de datos y nunca se muestran de vuelta.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Comportamiento en el POS</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Forma de pago que dispara el QR" className="max-w-md">
            <select className={fieldCls} value={f.forma_pago_id} onChange={(e) => set({ forma_pago_id: e.target.value })}>
              <option value="">— Selecciona —</option>
              {formas.map((fp) => <option key={fp.id} value={fp.id}>{fp.nombre}</option>)}
            </select>
          </Campo>
          <Switch checked={f.es_activo} onChange={(v) => set({ es_activo: v })} label="Cobros De Una activos" />
          <p className="text-xs text-muted-foreground">
            En el cobro, al elegir esa forma de pago aparece el botón «Generar QR»: el cliente escanea con su app,
            el sistema confirma el pago automáticamente y llena la referencia. En modo <span className="font-medium">Simulado</span> el
            pago se confirma solo a los ~10 segundos (para probar el circuito sin credenciales del banco).
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar configuración</Button>
      </div>
    </div>
  );
}
